import type { ReactElement, ReactNode } from 'react';
import { MINOR_NOTICE, type MinorNoticeBlock } from '../../../../../shared/minor-notice.js';
import './MinorNotice.css';

/**
 * The minor notice (2026-11-02 founder; edge age-check.js): what a person Ember closed as under 18 reads, wherever they
 * land on the web — the talk page of a stopped browser (create, an old link, a turn, the call page's redirect), the age
 * check's card and its /age page once they answered under 18, a guest Oriscen closed by hand (scripts/close-minor.mjs).
 * The words are shared/minor-notice.js, the same the apps draw from the age check's answer.
 */

/** **bold** and [label](url) — the only inline marks the notice uses. */
function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const mark = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
  let at = 0;
  let m: RegExpExecArray | null;
  while ((m = mark.exec(text))) {
    if (m.index > at) out.push(text.slice(at, m.index));
    if (m[1] !== undefined) out.push(<strong key={m.index}>{m[1]}</strong>);
    else {
      const href = m[3];
      out.push(href.startsWith('mailto:')
        ? <a key={m.index} href={href}>{m[2]}</a>
        : <a key={m.index} href={href} target="_blank" rel="noopener noreferrer">{m[2]}</a>);
    }
    at = m.index + m[0].length;
  }
  if (at < text.length) out.push(text.slice(at));
  return out;
}

/** Consecutive places are one list. */
function grouped(blocks: ReadonlyArray<Readonly<MinorNoticeBlock>>): Array<Readonly<MinorNoticeBlock> | ReadonlyArray<Readonly<MinorNoticeBlock>>> {
  const out: Array<Readonly<MinorNoticeBlock> | Array<Readonly<MinorNoticeBlock>>> = [];
  for (const b of blocks) {
    const last = out[out.length - 1];
    if (b.kind === 'li' && Array.isArray(last)) last.push(b);
    else out.push(b.kind === 'li' ? [b] : b);
  }
  return out;
}

export function MinorNotice(): ReactElement {
  return (
    <article className="minor-notice" role="status">
      <h1 className="minor-notice-title">{MINOR_NOTICE.title}</h1>
      {grouped(MINOR_NOTICE.blocks).map((g, i) => (
        Array.isArray(g)
          ? <ul key={i}>{g.map((b, j) => <li key={j}>{inline(b.text)}</li>)}</ul>
          : (g as MinorNoticeBlock).kind === 'h'
            ? <h2 key={i}>{(g as MinorNoticeBlock).text}</h2>
            : <p key={i}>{inline((g as MinorNoticeBlock).text)}</p>
      ))}
    </article>
  );
}
